import { Moment } from 'moment';
import { 
	Currencies, 
	CurrencySymbols, 
	NumberFormatStyle, 
	PortActionTypeLabels, 
	PortActionTypes } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatNumber } from '@shared/utils/formatNumber';
import { calculateTotal, round } from '@shared/utils/math';
import { toMoment } from '@shared/utils/date';
import { isPortCallCommencementModel } from '@shared/utils/portCallHelpers';
import { CargoWithPortsAndCalculationsFromDb } from '@api/features/laytime/getCargosAndLaytimes';
import Vessel from '@api/models/vessel';
import Voyage from '@api/models/voyage';
import PortCallAction from '@api/models/port-call-action';
import PortCall from '@api/models/port-call';
import RobBunker from '@api/models/rob-bunker';
import CargoPort from '@api/models/cargo-port';
import { CargoWithIncludes } from '../getTcPnl';

type PortActionType = (typeof PortActionTypes)[keyof typeof PortActionTypes];

export const actionsToShow = [
	PortActionTypes.COMMENCEMENT,
	PortActionTypes.COMPLETION,
	PortActionTypes.BUNKER_STEM,
	PortActionTypes.DELIVERY,
	PortActionTypes.REDELIVERY,
	PortActionTypes.REDELIVERING_TC_IN,
	PortActionTypes.ARRIVAL,
	PortActionTypes.DEPARTURE,
	PortActionTypes.LOADING,
	PortActionTypes.DISCHARGING,
	PortActionTypes.CANAL_TRANSIT,
] as Array<PortActionType>;

// Summary
export type SummaryItem = {
	label: string;
	value: string;
	bold?: boolean;
};

// Itinerary row
export type ItineraryTableRow = {
	actions: string;
	arrival: string;
	cargo: string;
	departure: string;
	distance: string;
	portName: string;
	speed: string;
	seaMargin: string;
};

export const daysBetween = (start?: Moment | null, end?: Moment | null) => {
	if (!start || !end) {
		return 0;
	}

	const diff = end.diff(start, 'hours', true) / 24;

	return diff > 0 ? diff : 0;
};
	
// P&L
export type PnlItemRow = {
	key: string;
	description: string;
	estimated: string;
	actual: string;
	variance: string;
	pct: string;
	bold?: boolean;
};

export type PnlValue = { estimated: number; actual: number; };

export const formatCurrencySigned = (
	value: number,
	currency: Values<typeof Currencies>,
) => {
	const symbol = CurrencySymbols[currency as keyof typeof CurrencySymbols] ?? currency;

	const abs = Math.abs(value);

	return value < 0 ?
		`-${symbol}${formatNumber(abs, {
			separateThousands: true,
		}, 2)}` :
		`${symbol}${formatNumber(abs, {
			separateThousands: true,
		}, 2)}`;
};

export const toPnlRow = (
	key: string,
	description: string,
	v: PnlValue | undefined,
	currency?: Values<typeof Currencies>,
): PnlItemRow => {
	const safe: PnlValue = v ?? { estimated: 0, actual: 0 };
	const varrianceAmount = variance(safe);

	return {
		key,
		description,
		estimated: 
			currency ? 
				formatCurrencySigned(safe.estimated ?? 0, currency) :
				formatNumber(safe.estimated, undefined, 2),
		actual: currency ? 
			formatCurrencySigned(safe.actual ?? 0, currency) : 
			formatNumber(safe.actual, undefined, 2),
		variance: currency ? 
			formatCurrencySigned(varrianceAmount, currency) :
			formatNumber(varrianceAmount, undefined, 2),
		pct: formatPct(pct(safe)),
		bold: 
			key === 'totalRevenue' || 
			key === 'totalExpenses' || 
			key === 'voyageProfit' || 
			key === 'voyageDays' || 
			key === 'tce' || 
			key === 'netResult' || 
			key === 'netResultPerDay',
	};
};

export const variance = (v: PnlValue) => (v.actual ?? 0) - (v.estimated ?? 0);

export const pct = (v: PnlValue): number | null => {
	const est = v.estimated ?? 0;
	const act = v.actual ?? 0;
	const varr = act - est;

	if (est === 0) {
		return varr === 0 ? 0 : null;
	}

	return (varr / est) * 100;
};

export const toNumberRow = (key: string, description: string, v: PnlValue | undefined): PnlItemRow => {
	const safe = v ?? { estimated: 0, actual: 0 };
	const varr = (safe.actual ?? 0) - (safe.estimated ?? 0);

	return {
		key,
		description,
		estimated: (safe.estimated ?? 0).toFixed(2),
		actual: (safe.actual ?? 0).toFixed(2),
		variance: varr.toFixed(2),
		pct: '—',
	};
};

export const formatPct = (value: number | null): string => {
	if (value == null || !isFinite(value)) {
		return '—';
	}

	const isNegativeZero =
		Object.is(value, -0) ||
    (value === 0 && Object.is(1 / value, -Infinity));

	const abs = Math.abs(value).toFixed(2);

	return (isNegativeZero || value < 0 ? '-' : '') + abs + '%';
};

export const formatNumberStyle = (value: number, style: NumberFormatStyle) => {
	const isNegative = Object.is(value, -0) || value < 0;
	const abs = Math.abs(value).toFixed(2);

	const withDecimal = style === NumberFormatStyle.European ?
		abs.replace('.', ',') :
		abs;

	return (isNegative ? '-' : '') + withDecimal;
};

// Cargos
export type CargoTableRow = {
	name: string;
	cpDate: string;
	blQuantity: string;
	blUnit: string;
	charterer: string;
	freightAmount: string;
	freightUnit: string;
	loadingPorts: string;
	dischargePorts: string;
};

type CargoProps = CargoWithPortsAndCalculationsFromDb & {
	Voyage: Voyage & {
		Vessel: Vessel;
	};
};

export const getCargoChartererNames = 
	(cargos: CargoWithIncludes[]): string => 
		cargos.map(c => c.FixtureCounterparty?.name)
			.filter((n): n is string => !!n)
			.join(', ');

type Action = Array<PortCallAction> & {
	Cargo?: CargoProps | null;
};

export const getCargo = (row: PortCall, actions: Action[]) => {
	if (row.cargoCommodity != null) {
		return row.cargoCommodity;
	}

	const cargos = [
		...new Set(
			actions
				.map((a: Action) => {
					const type = a.Cargo?.type;
					const quantity = a.Cargo?.quantity ?? 0;
					const unit = a.Cargo?.unit;

					if (!type) {
						return null;
					}

					return `${type} ${formatNumber(quantity, { separateThousands: true }, 2)} ${unit}`;
				})
				.filter(Boolean)
		),
	];

	return cargos;
};

export const getCargoDetails = (cargoPorts: CargoPort[], cargo: CargoWithIncludes) => {
	if (cargo == null) {
		return '';
	}

	const rowCargoPorts = cargoPorts ?? [];

	const totalBlQuantity = calculateTotal(
		rowCargoPorts,
		(cp) => cp.blQuantity ?? 0,
	);

	if (totalBlQuantity > 0) {
		return `${cargo.type ?? ''} ${formatNumber(
			totalBlQuantity,
			{ separateThousands: true },
			2,
		)} ${cargo.unit ?? ''}`;
	}

	const totalOverrideQuantity = calculateTotal(
		rowCargoPorts,
		(cp) => cp.laytimeQuantityOverride ?? 0,
	);

	if (totalOverrideQuantity > 0) {
		return `${cargo.type ?? ''} ${formatNumber(
			totalOverrideQuantity,
			{ separateThousands: true },
			2,
		)} ${cargo.unit ?? ''}`;
	}

	return `${cargo.type ?? ''}`.trim();
};

// Robs
export type PortCallActionWithRobBunkers = PortCallAction & { RobBunkers: Array<RobBunker> };

export type BunkerTableRow = {
	portName: string;
	robArrival: string;
	robDeparture: string;
};

export type RobBunkerLike = {
	fuelGrade: string;
	totalQuantity?: number | null;
	adjustedQuantity?: number | null;
};
	
export type RobRowData = {
	port: string;
	arrival: Record<string, RobValue>;
	departure: Record<string, RobValue>;
};

export const getRobBunkers = (row: PortCall, actions: PortCallActionWithRobBunkers[], fuels: string[]): RobRowData => ({
	port: row?.port?.name ?? '',
	arrival: buildRobMap(actions, [PortActionTypes.ARRIVAL], fuels),
	departure: buildRobMap(
		actions, [
			PortActionTypes.DEPARTURE, 
			PortActionTypes.COMMENCEMENT, 
			PortActionTypes.COMPLETION,
		], 
		fuels
	),
});

export type RobValue = { qty: number | null; estimated: boolean };

const getActualThenEstimatedAction = (actions: PortCallActionWithRobBunkers[]) => {
	const sorted = [...actions].sort(
		(a, b) => toMoment(b.date)?.valueOf() - toMoment(a.date)?.valueOf()
	);
	const actual = sorted.find((a) => !a.estimated) ?? null;
	const estimated = sorted.find((a) => a.estimated) ?? null;

	return { actual, estimated };
};

const buildRobMap = (
	actions: PortCallActionWithRobBunkers[],
	events: Array<Values<typeof PortActionTypes>>,
	fuels: string[],
): Record<string, RobValue> => {
	const actionEvents = actions.filter((a) => events.includes(a.event as Values<typeof PortActionTypes>));
	const { actual, estimated } = getActualThenEstimatedAction(actionEvents);

	return Object.fromEntries(
		fuels.map((fuelGrade) => {
			const actualRob = actual ?
				actual.RobBunkers.find((r) => r.fuelGrade === fuelGrade) :
				null;

			const estimatedRob = estimated ?
				estimated.RobBunkers.find((r) => r.fuelGrade === fuelGrade) :
				null;

			const rb = actualRob ?? estimatedRob ?? null;
			const quantity = rb ? (rb.adjustedQuantity ?? rb.totalQuantity ?? null) : null;

			return [
				fuelGrade,
				{
					qty: quantity == null ? null : round(quantity, 3),
					estimated: actualRob ? false : !!estimatedRob,
				},
			];
		}),
	);
};

export const getActionsData = (

	row: PortCall,
	actions: PortCallAction[],

) => {
	const isCommencement = isPortCallCommencementModel({ 
		...row,
		PortCallActions: actions,
	} as PortCall & { PortCallActions: PortCallAction[] });
	
	const filtered = actions.filter((a) => {
		if (a.event === PortActionTypes.ARRIVAL || a.event === PortActionTypes.DEPARTURE) {
			return false;
		}

		if (a.event === PortActionTypes.LOADING || a.event === PortActionTypes.DISCHARGING) {
			return true;
		}

		return actionsToShow.includes(a.event as PortActionType);
	});
	const actionLabels = filtered.map(
		(a) => PortActionTypeLabels[a.event as keyof typeof PortActionTypeLabels] || a.event,
	);

	if (row.cargoActivity) {
		actionLabels.push(row.cargoActivity);
	}

	return { actionLabels, isCommencement };
};
